import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Star, CheckCircle, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ name: '', rating: 5, text: '' });
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const res = await fetch(`${API}/api/reviews`);
      const data = await res.json();
      setReviews(data);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmitStatus('submitting');
    try {
      const res = await fetch(`${API}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewForm)
      });
      if (res.ok) {
        setSubmitStatus('success');
        setReviewForm({ name: '', rating: 5, text: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      setSubmitStatus('error');
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Default testimonials
  const defaultTestimonials = [
    {
      id: 'default-1',
      name: 'Sarah M.',
      location: 'Montclair, NJ',
      rating: 5,
      text: "Finally, someone who explains what my home actually needs — without trying to upsell me. The Home Profile gave me a clear roadmap."
    },
    {
      id: 'default-2',
      name: 'John D.',
      location: 'Edison, NJ',
      rating: 5,
      text: "As a first-time homeowner, I had no idea what to prioritize. Valenrock made it simple. Worth every penny."
    },
    {
      id: 'default-3',
      name: 'Michael R.',
      location: 'Summit, NJ',
      rating: 5,
      text: "Responsive, honest, and no subscription traps. Exactly what I was looking for. Highly recommend!"
    }
  ];

  const allReviews = [...defaultTestimonials, ...reviews.map(r => ({ ...r, location: 'NJ' }))];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-20 md:py-24 bg-[#003366] text-white overflow-hidden mt-20">
        {/* Blueprint pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }}></div>
        {/* Logo Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img src="https://customer-assets.emergentagent.com/job_waitlist-system-5/artifacts/0d36dd51_Transparent%20Png-02.png" alt="" className="w-[400px] md:w-[500px] h-auto opacity-[0.06]" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="mb-6">
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-2">
              Customer
            </span>
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
              <span className="text-[#CFB973]">Reviews</span>
            </span>
          </h1>
          
          {/* Decorative line */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
            <div className="w-2 h-2 bg-[#CFB973] rotate-45"></div>
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
          </div>
          
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
            See what homeowners are saying about <span className="text-[#CFB973] font-semibold">Valenrock™ Home Services</span>
          </p>
          
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-[#CFB973] hover:bg-[#b8a55e] text-[#003366] font-bold px-8 py-6 text-lg shadow-xl"
            data-testid="leave-review-btn"
          >
            <Star className="mr-2" size={18} /> Leave a Review
          </Button>
        </div>
      </section>

      {/* Review Form Section */}
      {showForm && (
        <section className="bg-white border-b">
          <div className="max-w-2xl mx-auto px-4 py-10">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#003366]">Share Your Experience</h2>
              <button onClick={() => { setShowForm(false); setSubmitStatus(null); }} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            {submitStatus === 'success' ? (
              <div className="text-center py-8 bg-green-50 rounded-xl">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-xl font-semibold text-gray-700">Thank you for your review!</p>
                <p className="text-gray-500 mt-2">It will appear on our site after approval.</p>
                <Button 
                  onClick={() => { setShowForm(false); setSubmitStatus(null); }}
                  className="mt-6 bg-[#003366] text-white"
                >
                  Close
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmitReview} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">Your Name</label>
                  <input
                    id="name"
                    type="text"
                    required
                    value={reviewForm.name}
                    onChange={(e) => setReviewForm({...reviewForm, name: e.target.value})}
                    className="block w-full border border-gray-300 rounded-lg px-4 py-3"
                    placeholder="John D."
                    autoComplete="name"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Rating</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewForm({...reviewForm, rating: star})}
                        className="p-1 transition-transform hover:scale-110"
                      >
                        <Star 
                          size={36} 
                          className={star <= reviewForm.rating ? 'text-[#CFB973] fill-[#CFB973]' : 'text-gray-300'} 
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="review" className="block text-gray-700 font-semibold mb-2">Your Review</label>
                  <textarea
                    id="review"
                    required
                    value={reviewForm.text}
                    onChange={(e) => setReviewForm({...reviewForm, text: e.target.value})}
                    rows={5}
                    className="block w-full border border-gray-300 rounded-lg px-4 py-3 resize-none"
                    placeholder="Tell us about your experience with Valenrock..."
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#003366] hover:bg-[#002244] text-white py-3 text-lg"
                  disabled={submitStatus === 'submitting'}
                >
                  {submitStatus === 'submitting' ? 'Submitting...' : 'Submit Review'}
                </Button>

                {submitStatus === 'error' && (
                  <p className="text-red-500 text-center">Something went wrong. Please try again.</p>
                )}
              </form>
            )}
          </div>
        </section>
      )}

      {/* Reviews Grid */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allReviews.map((review) => (
              <div key={review.id} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={i < review.rating ? "text-[#CFB973] fill-[#CFB973]" : "text-gray-300"} 
                      size={20} 
                    />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic leading-relaxed">
                  "{review.text}"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#003366] flex items-center justify-center text-white font-bold">
                    {getInitials(review.name)}
                  </div>
                  <div>
                    <p className="font-bold text-[#003366]">{review.name}</p>
                    {review.location && <p className="text-gray-500 text-sm">{review.location}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {allReviews.length === 0 && (
            <div className="text-center py-16 text-gray-500">
              <Star className="mx-auto mb-4 text-gray-300" size={48} />
              <p className="text-xl">No reviews yet. Be the first to share your experience!</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#003366] py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Ready to Experience the Valenrock Difference?</h2>
          <p className="text-white/80 mb-6">Book your Whole-Home Assessment today.</p>
          <Button 
            onClick={() => window.location.href = '/assessment'}
            className="bg-[#CFB973] hover:bg-[#b8a55e] text-[#003366] font-semibold px-8 py-3"
          >
            Get Started
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ReviewsPage;
